import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const medicalGeneticFieldPack: FieldPack = {
  id: 'genogram-medical-genetic',
  name: 'Medical & Genetic History',
  description: 'Fields for tracking medical conditions and genetic information',
  version: '1.0.0',
  dependencies: ['genogram-base'],
  fields: [
    {
      id: 'medicalConditionTags',
      label: 'fields.medical.medicalConditionTags.label',
      description: 'fields.medical.medicalConditionTags.description',
      type: FieldType.MULTI_SELECT,
      tags: ['medical', 'health', 'mental-health'],
      priority: 100,
      allowCustomOptions: true,
      options: [
        { value: 'diabetes', label: 'fields.medical.medicalConditionTags.options.diabetes' },
        { value: 'hypertension', label: 'fields.medical.medicalConditionTags.options.hypertension' },
        { value: 'heart_disease', label: 'fields.medical.medicalConditionTags.options.heart_disease' },
        { value: 'cancer', label: 'fields.medical.medicalConditionTags.options.cancer' },
        { value: 'asthma', label: 'fields.medical.medicalConditionTags.options.asthma' },
        { value: 'alzheimers', label: 'fields.medical.medicalConditionTags.options.alzheimers' },
        { value: 'depression', label: 'fields.medical.medicalConditionTags.options.depression' },
        { value: 'anxiety', label: 'fields.medical.medicalConditionTags.options.anxiety' },
        { value: 'bipolar', label: 'fields.medical.medicalConditionTags.options.bipolar' },
        { value: 'schizophrenia', label: 'fields.medical.medicalConditionTags.options.schizophrenia' },
        { value: 'autism', label: 'fields.medical.medicalConditionTags.options.autism' },
        { value: 'adhd', label: 'fields.medical.medicalConditionTags.options.adhd' },
        { value: 'down_syndrome', label: 'fields.medical.medicalConditionTags.options.down_syndrome' },
        { value: 'anemia', label: 'fields.medical.medicalConditionTags.options.anemia' },
        { value: 'albinism', label: 'fields.medical.medicalConditionTags.options.albinism' },
        { value: 'obesity', label: 'fields.medical.medicalConditionTags.options.obesity' },
      ],
      namespace: 'medical',
    },
    {
      id: 'causeOfDeath',
      label: 'fields.medical.causeOfDeath.label',
      description: 'fields.medical.causeOfDeath.description',
      type: FieldType.TEXT,
      tags: ['medical', 'health', 'timeline'],
      priority: 95,
      placeholder: 'fields.medical.causeOfDeath.placeholder',
      namespace: 'medical',
    },
    {
      id: 'medications',
      label: 'fields.medical.medications.label',
      description: 'fields.medical.medications.description',
      type: FieldType.TEXT,
      tags: ['medical', 'health'],
      priority: 90,
      multiline: true,
      placeholder: 'fields.medical.medications.placeholder',
      namespace: 'medical',
    },
    {
      id: 'allergies',
      label: 'fields.medical.allergies.label',
      description: 'fields.medical.allergies.description',
      type: FieldType.TEXT,
      tags: ['medical', 'health'],
      priority: 85,
      multiline: true,
      placeholder: 'fields.medical.allergies.placeholder',
      namespace: 'medical',
    },
    {
      id: 'surgeries',
      label: 'fields.medical.surgeries.label',
      description: 'fields.medical.surgeries.description',
      type: FieldType.TEXT,
      tags: ['medical', 'health', 'timeline'],
      priority: 80,
      multiline: true,
      placeholder: 'fields.medical.surgeries.placeholder',
      namespace: 'medical',
    },
    {
      id: 'immunizations',
      label: 'fields.medical.immunizations.label',
      description: 'fields.medical.immunizations.description',
      type: FieldType.SELECT,
      tags: ['medical', 'health'],
      priority: 75,
      options: [
        { value: 'up_to_date', label: 'fields.medical.immunizations.options.up_to_date' },
        { value: 'partial', label: 'fields.medical.immunizations.options.partial' },
        { value: 'unknown', label: 'fields.medical.immunizations.options.unknown' },
      ],
      namespace: 'medical',
    },
    {
      id: 'geneticMarkers',
      label: 'fields.medical.geneticMarkers.label',
      description: 'fields.medical.geneticMarkers.description',
      type: FieldType.MULTI_SELECT,
      tags: ['genetic', 'hereditary'],
      priority: 70,
      allowCustomOptions: true,
      options: [
        { value: 'brca1', label: 'fields.medical.geneticMarkers.options.brca1' },
        { value: 'brca2', label: 'fields.medical.geneticMarkers.options.brca2' },
        { value: 'apoe4', label: 'fields.medical.geneticMarkers.options.apoe4' },
        { value: 'factor_v', label: 'fields.medical.geneticMarkers.options.factor_v' },
        { value: 'huntingtons', label: 'fields.medical.geneticMarkers.options.huntingtons' },
        { value: 'sickle_cell', label: 'fields.medical.geneticMarkers.options.sickle_cell' },
        { value: 'carrier', label: 'fields.medical.geneticMarkers.options.carrier' },
        { value: 'affected', label: 'fields.medical.geneticMarkers.options.affected' },
        { value: 'possibly_affected', label: 'fields.medical.geneticMarkers.options.possibly_affected' },
        { value: 'suspected_affected', label: 'fields.medical.geneticMarkers.options.suspected_affected' },
      ],
      namespace: 'medical',
    },
    {
      id: 'screening',
      label: 'fields.medical.screening.label',
      description: 'fields.medical.screening.description',
      type: FieldType.SELECT,
      tags: ['genetic', 'hereditary'],
      priority: 65,
      options: [
        { value: 'completed', label: 'fields.medical.screening.options.completed' },
        { value: 'recommended', label: 'fields.medical.screening.options.recommended' },
        { value: 'declined', label: 'fields.medical.screening.options.declined' },
        { value: 'not_applicable', label: 'fields.medical.screening.options.not_applicable' },
      ],
      namespace: 'medical',
    },
    {
      id: 'substanceUse',
      label: 'fields.medical.substanceUse.label',
      description: 'fields.medical.substanceUse.description',
      type: FieldType.MULTI_SELECT,
      tags: ['mental-health', 'behavioral'],
      priority: 60,
      options: [
        { value: 'tobacco', label: 'fields.medical.substanceUse.options.tobacco' },
        { value: 'alcohol', label: 'fields.medical.substanceUse.options.alcohol' },
        { value: 'cannabis', label: 'fields.medical.substanceUse.options.cannabis' },
        { value: 'prescription', label: 'fields.medical.substanceUse.options.prescription' },
        { value: 'illicit', label: 'fields.medical.substanceUse.options.illicit' },
        { value: 'language_problem', label: 'fields.medical.substanceUse.options.language_problem' },
        { value: 'confirmed_alcohol_drug_abuse', label: 'fields.medical.substanceUse.options.confirmed_alcohol_drug_abuse' },
        { value: 'suspected_alcohol_drug_abuse', label: 'fields.medical.substanceUse.options.suspected_alcohol_drug_abuse' },
        { value: 'recovering_from_alcohol_drug_abuse', label: 'fields.medical.substanceUse.options.recovering_from_alcohol_drug_abuse' },
        { value: 'drug_alcohol_abuse_with_physical_or_mental_problem', label: 'fields.medical.substanceUse.options.drug_alcohol_abuse_with_physical_or_mental_problem' },
        { value: 'recovering_from_drug_alcohol_abuse_with_physical_or_mental_problem', label: 'fields.medical.substanceUse.options.recovering_from_drug_alcohol_abuse_with_physical_or_mental_problem' },
        { value: 'none', label: 'fields.medical.substanceUse.options.none' },
      ],
      namespace: 'medical',
    },
    {
      id: 'medicalNotes',
      label: 'fields.medical.medicalNotes.label',
      description: 'fields.medical.medicalNotes.description',
      type: FieldType.RICH_TEXT,
      tags: ['notes', 'documentation'],
      priority: 50,
      multiline: true,
      placeholder: 'fields.medical.medicalNotes.placeholder',
      namespace: 'medical',
    },
  ],
};
